import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowUp, Bot, Loader2, MessageSquareText, User } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { cn } from "@/lib/utils";

const TYPING_SPEED_MS = 22;
const ASSISTANT_PAUSE_MS = 1200;
const RESPONSE_TYPING_SPEED_MS = 28;
const RESPONSE_CHUNK_PAUSE_MS = 520;
const LOOP_PAUSE_MS = 2400;

export const LandingChatDemo = () => {
  const { language } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [composerText, setComposerText] = useState("");
  const [isAssistantTyping, setIsAssistantTyping] = useState(false);
  const [cycle, setCycle] = useState(0);
  const [hasActivated, setHasActivated] = useState(false);
  const wrapperRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const timersRef = useRef([]);
  const assistantDraftRef = useRef("");

  const demo = useMemo(() => {
    if (language === "es") {
      return {
        title: "Asistente de investigación IA",
        subtitle: "Pregunta sobre el contenido y combina el contexto del video con fuentes verificadas.",
        welcome: "Hola — ya tengo el contexto del video listo. Hazme una pregunta y te respondo con base en el contenido y, si hace falta, con fuentes verificadas.",
        thinking: "Analizando transcripción y fuentes…",
        send: "Enviar",
        webUsed: "Web pública usada",
        queryLabel: "Query de búsqueda",
        chatPrompt: "Escribe tu pregunta aquí...",
        scenarios: [
          {
            user: "Explícame el artículo 457 que sale en el video",
            searchQuery: "artículo 457 código de procedimiento penal colombiano",
            answerChunks: [
              `A) Lo que sí menciona el video sobre el artículo 457\n\n- [07:57] artículo 457 de la ley 906\n- [08:56] "...este defensor se reserva ese derecho de proponer la nulidad del 457, que es una violación sustancial al debido proceso."`,
              `B) Qué es o de qué trata según las fuentes verificadas\n\nLas fuentes verificadas ubican el artículo 457 dentro del Código de Procedimiento Penal colombiano y lo presentan como causal de nulidad por violación a garantías fundamentales.`,
              `C) Extractos verificados clave\n\n[1] Leyes.co — Código de Procedimiento Penal — artículo 457\n[2] Ley 906 de 2004 — artículo 457`,
            ],
            sources: [
              {
                id: "1",
                label: "Leyes.co — Código de Procedimiento Penal — artículo 457",
                url: "https://leyes.co/codigo_de_procedimiento_penal/457.htm",
              },
              {
                id: "2",
                label: "Ley 906 de 2004 — artículo 457",
                url: "http://www.secretariasenado.gov.co/senado/basedoc/ley_0906_2004_pr011.html",
              },
            ],
          },
          {
            user: "Háblame más sobre la mitocondria y trae información con fuentes; relaciónalo con el video.",
            searchQuery: "mitocondria ATP función fuentes verificadas",
            answerChunks: [
              `A) Lo que sí menciona el video\n\n- [03:11] se habla del metabolismo celular\n- [11:24] se menciona que la energía disponible cambia cuando falla la producción de ATP`,
              `B) Qué es la mitocondria según las fuentes verificadas\n\nLa mitocondria es el orgánulo que produce la mayor parte del ATP celular. Relacionándolo con el video, ayuda a entender por qué el cambio en metabolismo y energía altera el funcionamiento de la célula.`,
              `C) Extractos verificados clave\n\n[1] Encyclopaedia Britannica — mitochondrion\n[2] NCBI Bookshelf — Mitochondria`,
            ],
            sources: [
              {
                id: "1",
                label: "Encyclopaedia Britannica — mitochondrion",
                url: "https://www.britannica.com/science/mitochondrion",
              },
              {
                id: "2",
                label: "NCBI Bookshelf — Mitochondria",
                url: "https://www.ncbi.nlm.nih.gov/books/NBK9896/",
              },
            ],
          },
          {
            user: "¿Cuál es la idea principal del documental sobre los océanos y qué fuentes la respaldan?",
            searchQuery: "documental océanos contaminación fuentes NOAA UN",
            answerChunks: [
              `A) Lo que sí menciona el video\n\n- [01:42] el documental conecta la pesca industrial con pérdida de biodiversidad\n- [12:08] se menciona que el calentamiento y el plástico agravan la degradación del ecosistema`,
              `B) Qué respaldan las fuentes verificadas\n\nLas fuentes externas coinciden en que el océano está bajo presión por calentamiento, acidificación, sobrepesca y contaminación plástica. Eso refuerza la tesis central del documental.`,
              `C) Extractos verificados clave\n\n[1] NOAA — Ocean climate indicators\n[2] UN Environment Programme — marine pollution`,
            ],
            sources: [
              {
                id: "1",
                label: "NOAA — Ocean climate indicators",
                url: "https://sanctuaries.noaa.gov/science/conservation/ocean-climate-indicators.html",
              },
              {
                id: "2",
                label: "UN Environment Programme — marine pollution",
                url: "http://sdgs.unep.org/article/sdg-indicator-1411",
              },
            ],
          },
        ],
      };
    }

    return {
      title: "AI research assistant",
      subtitle: "Ask about the content and combine video context with verified outside sources.",
      welcome: "Hi — I already have the video context ready. Ask a question and I’ll answer from the content and, when needed, with verified sources.",
      thinking: "Analyzing transcript and sources…",
      send: "Send",
      webUsed: "Public web used",
      queryLabel: "Search query",
      chatPrompt: "Type your question here...",
      scenarios: [
        {
          user: "Explain article 457 that appears in the video.",
          searchQuery: "article 457 colombian criminal procedure code",
          answerChunks: [
            `A) What the video explicitly mentions about article 457\n\n- [07:57] article 457 of Law 906\n- [08:56] "...the defense reserves the right to raise nullity under 457, as a substantial violation of due process."`,
            `B) What it is about according to verified sources\n\nVerified sources place article 457 inside the Colombian Criminal Procedure Code and describe it as nullity for violations of fundamental guarantees.`,
            `C) Key verified excerpts\n\n[1] Leyes.co — article 457\n[2] Law 906 of 2004 — article 457`,
          ],
          sources: [
            {
              id: "1",
              label: "Leyes.co — Criminal Procedure Code — article 457",
              url: "https://leyes.co/codigo_de_procedimiento_penal/457.htm",
            },
            {
              id: "2",
              label: "Law 906 of 2004 — article 457",
              url: "http://www.secretariasenado.gov.co/senado/basedoc/ley_0906_2004_pr011.html",
            },
          ],
        },
        {
          user: "Tell me more about mitochondria, bring sources, and connect it to the video.",
          searchQuery: "mitochondria ATP function verified sources",
          answerChunks: [
            `A) What the video explicitly mentions\n\n- [03:11] the speaker refers to cellular metabolism\n- [11:24] the video mentions that available energy changes when ATP production drops`,
            `B) What mitochondria are according to verified sources\n\nThe mitochondrion is the organelle responsible for producing most cellular ATP. Connected to the video, it helps explain why changes in metabolism and energy availability affect cell function.`,
            `C) Key verified excerpts\n\n[1] Encyclopaedia Britannica — mitochondrion\n[2] NCBI Bookshelf — Mitochondria`,
          ],
          sources: [
            {
              id: "1",
              label: "Encyclopaedia Britannica — mitochondrion",
              url: "https://www.britannica.com/science/mitochondrion",
            },
            {
              id: "2",
              label: "NCBI Bookshelf — Mitochondria",
              url: "https://www.ncbi.nlm.nih.gov/books/NBK9896/",
            },
          ],
        },
        {
          user: "What is the main idea of the ocean documentary, and which sources support it?",
          searchQuery: "ocean documentary pollution overfishing verified sources",
          answerChunks: [
            `A) What the video explicitly mentions\n\n- [01:42] the documentary connects industrial fishing to biodiversity loss\n- [12:08] it explains how warming and plastic intensify ecosystem damage`,
            `B) What verified sources support\n\nExternal sources agree that the ocean is under pressure from warming, acidification, overfishing, and plastic pollution. That directly reinforces the documentary’s main argument.`,
            `C) Key verified excerpts\n\n[1] NOAA — Ocean climate indicators\n[2] UN Environment Programme — marine pollution`,
          ],
          sources: [
            {
              id: "1",
              label: "NOAA — Ocean climate indicators",
              url: "https://sanctuaries.noaa.gov/science/conservation/ocean-climate-indicators.html",
            },
            {
              id: "2",
              label: "UN Environment Programme — marine pollution",
              url: "http://sdgs.unep.org/article/sdg-indicator-1411",
            },
          ],
        },
      ],
    };
  }, [language]);

  const wait = (duration) =>
    new Promise((resolve) => {
      const timer = setTimeout(resolve, duration);
      timersRef.current.push(timer);
    });

  const scrollMessagesToBottom = (behavior = "smooth") => {
    if (!messagesContainerRef.current) {
      return;
    }

    messagesContainerRef.current.scrollTo({
      top: messagesContainerRef.current.scrollHeight,
      behavior,
    });
  };

  useEffect(() => {
    const node = wrapperRef.current;
    if (!node || hasActivated) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setCycle(0);
          setHasActivated(true);
        }
      },
      { threshold: 0.35 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasActivated]);

  useEffect(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current = [];

    setMessages([{ role: "assistant", content: demo.welcome }]);
    setComposerText("");
    setIsAssistantTyping(false);
    assistantDraftRef.current = "";

    if (!hasActivated) {
      return undefined;
    }

    const scenario = demo.scenarios[cycle % demo.scenarios.length];

    let cancelled = false;

    const runDemo = async () => {
      await wait(1200);
      if (cancelled) return;

      for (let index = 0; index < scenario.user.length; index += 1) {
        if (cancelled) return;
        setComposerText(scenario.user.slice(0, index + 1));
        await wait(TYPING_SPEED_MS);
      }

      if (cancelled) return;
      setMessages((prev) => [...prev, { role: "user", content: scenario.user }]);
      setComposerText("");
      requestAnimationFrame(() => scrollMessagesToBottom("smooth"));

      await wait(500);
      if (cancelled) return;
      setIsAssistantTyping(true);
      requestAnimationFrame(() => scrollMessagesToBottom("smooth"));

      await wait(ASSISTANT_PAUSE_MS);
      if (cancelled) return;
      setIsAssistantTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "",
          usedExternalSearch: true,
          externalSearchQuery: scenario.searchQuery,
          sources: scenario.sources,
        },
      ]);
      assistantDraftRef.current = "";
      requestAnimationFrame(() => scrollMessagesToBottom("smooth"));

      for (let chunkIndex = 0; chunkIndex < scenario.answerChunks.length; chunkIndex += 1) {
        const chunk = scenario.answerChunks[chunkIndex];
        const prefix = chunkIndex === 0 ? "" : "\n\n";
        const chunkText = `${prefix}${chunk}`;

        for (let charIndex = 0; charIndex < chunkText.length; charIndex += 1) {
          if (cancelled) return;
          const nextChar = chunkText[charIndex];
          assistantDraftRef.current = `${assistantDraftRef.current}${nextChar}`;
          setMessages((prev) => {
            const next = [...prev];
            const lastMessage = next[next.length - 1];
            if (lastMessage?.role === "assistant") {
              next[next.length - 1] = {
                ...lastMessage,
                content: assistantDraftRef.current,
              };
            }
            return next;
          });

          if (charIndex % 6 === 0) {
            requestAnimationFrame(() => scrollMessagesToBottom("smooth"));
          }

          await wait(RESPONSE_TYPING_SPEED_MS);
        }

        requestAnimationFrame(() => scrollMessagesToBottom("smooth"));
        await wait(RESPONSE_CHUNK_PAUSE_MS);
      }

      await wait(LOOP_PAUSE_MS);
      if (!cancelled) {
        setCycle((prev) => prev + 1);
      }
    };

    runDemo();

    return () => {
      cancelled = true;
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current = [];
    };
  }, [cycle, demo, hasActivated]);

  return (
    <div ref={wrapperRef} className="mx-auto w-full max-w-3xl min-w-0" data-testid="landing-how-step-05-chat-demo-wrapper">
      <div
        className={cn(
          "overflow-hidden rounded-[28px] border border-border/70 bg-background shadow-2xl shadow-black/40"
        )}
        data-testid="landing-how-step-05-chat-demo-card"
      >
        <div className="border-b border-border/60 bg-background/95 px-6 py-4 backdrop-blur-xl sm:px-8">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-border/60 bg-card/80 shadow-sm shadow-black/20">
                  <MessageSquareText className="h-4.5 w-4.5 text-primary" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold tracking-tight text-foreground" data-testid="landing-how-step-05-chat-title">
                    {demo.title}
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-muted-foreground" data-testid="landing-how-step-05-chat-subtitle">
                    {demo.subtitle}
                  </p>
                </div>
              </div>
            </div>

            <Badge variant="outline" className="rounded-full border-border/60 bg-muted/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground" data-testid="landing-how-step-05-chat-usage-badge">
              Professional / Enterprise
            </Badge>
          </div>
        </div>

        <div className="flex h-[500px] flex-col overflow-hidden sm:h-[560px]">
          <div ref={messagesContainerRef} className="demo-scroll-hidden pointer-events-none flex-1 overflow-y-auto px-6 py-5 select-none sm:px-8 lg:px-10" data-testid="landing-how-step-05-chat-messages-container">
            <div className="flex w-full flex-col gap-5">
              {messages.map((message, index) => (
                <div key={`${message.role}-${index}`} className={cn("flex", message.role === "user" ? "justify-end" : "justify-start")}>
                  <div className="min-w-0 max-w-[96%] xl:max-w-[92%]">
                    <div className="mb-1.5 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                      {message.role === "user" ? <User className="h-3.5 w-3.5" strokeWidth={1.5} /> : <Bot className="h-3.5 w-3.5 text-primary" strokeWidth={1.5} />}
                      <span>{message.role === "user" ? "You" : "AI"}</span>
                    </div>
                    <div
                      className={cn(
                        "min-w-0 break-words rounded-2xl border px-5 py-4 text-base shadow-sm sm:text-[1.02rem]",
                        message.role === "user"
                          ? "rounded-tr-sm border-primary/25 bg-primary/10 text-foreground"
                          : "rounded-tl-sm border-border/60 bg-card text-card-foreground"
                      )}
                      data-testid={message.role === "user" ? "landing-how-step-05-chat-question" : "landing-how-step-05-chat-answer"}
                    >
                      <p className="whitespace-pre-wrap text-base leading-8 text-foreground sm:text-[1.02rem]">{message.content}</p>
                    </div>

                    {message.role === "assistant" && message.usedExternalSearch && (
                      <div className="mt-2 flex min-w-0 flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                        <Badge variant="outline" className="rounded-full border-primary/25 bg-primary/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-primary" data-testid="landing-how-step-05-chat-external-search-badge">
                          {demo.webUsed}
                        </Badge>
                        <Badge variant="outline" className="block max-w-full min-w-0 truncate rounded-full border-border/60 bg-muted/30 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-muted-foreground sm:max-w-[340px]" data-testid="landing-how-step-05-chat-search-query-badge">
                          {demo.queryLabel}: {message.externalSearchQuery}
                        </Badge>
                      </div>
                    )}

                    {message.role === "assistant" && Array.isArray(message.sources) && message.sources.length > 0 && (
                      <div className="mt-3 rounded-2xl border border-border/60 bg-muted/20 p-3" data-testid="landing-how-step-05-chat-sources-list">
                        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                          {language === "es" ? "Fuentes verificadas" : "Verified sources"}
                        </p>
                        <div className="space-y-2 text-sm leading-6 text-muted-foreground">
                          {message.sources.map((source) => (
                            <a
                              key={source.id}
                              href={source.url}
                              target="_blank"
                              rel="noreferrer"
                              className="block break-words text-primary underline decoration-primary/30 underline-offset-4 transition-colors hover:text-primary/80"
                              data-testid={`landing-how-step-05-chat-source-link-${source.id}`}
                            >
                              [{source.id}] {source.label}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {isAssistantTyping && (
                <div className="flex justify-start">
                  <div className="rounded-2xl rounded-tl-sm border border-border/60 bg-card px-5 py-4 text-base text-muted-foreground shadow-sm sm:text-[1.02rem]" data-testid="landing-how-step-05-chat-typing-indicator">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      <span>{demo.thinking}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="shrink-0 border-t border-border/60 bg-background px-6 py-4 sm:px-8 lg:px-10">
            <div className="w-full">
              <div className="rounded-[22px] border border-border/60 bg-muted/20 p-1.5 shadow-sm shadow-black/15 transition-all">
                <div className="min-h-[64px] max-h-[112px] overflow-hidden px-3 py-2.5 pr-14 text-base leading-6 text-foreground sm:text-[1.02rem]" data-testid="landing-how-step-05-chat-composer">
                  <span className={composerText ? "text-foreground" : "text-muted-foreground/70"}>{composerText || demo.chatPrompt}</span>
                </div>
                <div className="flex justify-end pr-1 pb-1">
                  <Button type="button" className="h-9 w-9 rounded-full bg-primary p-0 text-primary-foreground transition-colors hover:bg-primary/90" data-testid="landing-how-step-05-chat-send-btn" aria-label={demo.send}>
                    <ArrowUp className="h-4 w-4" strokeWidth={2.2} />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};